import { supabase } from 'infrastructure/utils/supabase.js';
import axios from 'infrastructure/utils/axios.js';
import { AUTH_ENDPOINTS } from '../../configs/auth.config.js';
import { setSession } from 'infrastructure/utils/jwt.js';

const normalizeRole = (value) => {
  const role = String(value || 'authenticated').toLowerCase();
  if (['admin', 'superadmin', 'super_admin', 'entreprise', 'controleur', 'accountant', 'support', 'service_role'].includes(role)) {
    return 'admin';
  }
  if (['driver', 'chauffeur'].includes(role)) {
    return 'chauffeur';
  }
  return role;
};

const buildSupabaseUserProfile = (user) => ({
  id: user?.id,
  email: user?.email,
  role: normalizeRole(user?.user_metadata?.role || user?.app_metadata?.role),
  nom: user?.user_metadata?.nom || '',
  prenom: user?.user_metadata?.prenom || '',
  user_metadata: user?.user_metadata || {},
  app_metadata: user?.app_metadata || {},
  ...user?.user_metadata,
  ...user?.app_metadata
});

const mergeBackendProfile = (supabaseProfile, backendProfile) => {
  if (!backendProfile || typeof backendProfile !== 'object') {
    return supabaseProfile;
  }

  return {
    ...supabaseProfile,
    ...backendProfile,
    id: backendProfile.id || backendProfile.userId || supabaseProfile.id,
    email: backendProfile.email || supabaseProfile.email,
    role: normalizeRole(
      backendProfile.role ||
      backendProfile.metadata?.role ||
      backendProfile.metadata?.rawAppMetaData?.role ||
      backendProfile.metadata?.rawUserMetaData?.role ||
      supabaseProfile.role
    ),
    nom: backendProfile.nom || supabaseProfile.nom,
    prenom: backendProfile.prenom || supabaseProfile.prenom
  };
};

/**
 * Service d'authentification pour TxApp
 * Utilise Supabase Auth natif
 */

/**
 * Connexion d'un utilisateur avec Supabase Auth
 * @param {string} username - Email de l'utilisateur
 * @param {string} password - Mot de passe
 * @returns {Promise<{user: Object, session: Object}>}
 */
export const loginUser = async (username, password) => {
  try {

    // Utiliser Supabase Auth natif
    const { data, error } = await supabase.auth.signInWithPassword({
      email: username,
      password: password
    });

    if (error) {
      throw new Error(error.message || 'Erreur de connexion');
    }

    if (data.session?.access_token) {
      setSession(data.session.access_token);
    }

    const supabaseProfile = buildSupabaseUserProfile(data.user);

    let userProfile = supabaseProfile;
    try {
      const profileResponse = await axios.get(`/api/utilisateurs/${data.user.id}`);
      const profileResult = profileResponse.data;
      const backendProfile = profileResult.success ? profileResult.data : profileResult;
      userProfile = mergeBackendProfile(supabaseProfile, backendProfile);
    } catch {
      userProfile = supabaseProfile;
    }

    const result = {
      user: userProfile,
      session: data.session,
      token: data.session.access_token, // Pour compatibilité temporaire
      success: true
    };

    return result;
  } catch (error) {
    throw new Error(error.message || 'Erreur de connexion');
  }
};

/**
 * Récupérer le profil utilisateur depuis Supabase + API
 * @param {string} userId - ID de l'utilisateur (UUID)
 * @returns {Promise<Object>}
 */
export const getUserProfile = async (userId) => {
  try {
    void userId;

    // 1. Vérifier la session Supabase
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError) {
      throw new Error('Session expirée');
    }

    const supabaseProfile = buildSupabaseUserProfile(user);

    try {
      const response = await axios.get(`/api/utilisateurs/${user?.id}`);
      const result = response.data;
      const backendProfile = result.success ? result.data : result;
      return mergeBackendProfile(supabaseProfile, backendProfile);
    } catch {
      return supabaseProfile;
    }
  } catch {
    throw new Error('Erreur lors de la récupération du profil utilisateur');
  }
};

/**
 * Vérifier la validité d'un token
 * @param {string} token - Token JWT
 * @returns {Promise<Object>}
 */
export const verifyToken = async (token) => {
  try {
    const response = await axios.post(AUTH_ENDPOINTS.VERIFY, {
      token
    }, {
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (!response.data.success) {
      throw new Error(response.data.error || 'Token invalide');
    }

    return response.data;
  } catch {
    throw new Error('Token invalide');
  }
};

/**
 * Créer un nouvel utilisateur
 * @param {Object} userData - Données de l'utilisateur
 * @returns {Promise<Object>}
 */
export const createUser = async (userData) => {
  try {
    const response = await axios.post('/api/utilisateurs', userData, {
      headers: {
        'Content-Type': 'application/json'
      }
    });

    const result = response.data;
    return result.success ? result.data : result;
  } catch {
    throw new Error('Erreur lors de la création de l\'utilisateur');
  }
};

/**
 * Inscrire un administrateur et sa société
 * @param {Object} adminData - Données de l'administrateur et de sa société
 * @returns {Promise<Object>}
 */
export const registerAdmin = async (adminData) => {
  try {
    const response = await axios.post('/api/auth/signup', adminData, {
      headers: {
        'Content-Type': 'application/json'
      }
    });

    const result = response.data;
    return result.success ? result.data : result;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Erreur lors de l\'inscription de l\'administrateur');
  }
};

/**
 * Changer le mot de passe
 * @param {Object} passwordData - Données du changement de mot de passe
 * @returns {Promise<Object>}
 */
export const changePassword = async (passwordData) => {
  try {
    const response = await axios.post(AUTH_ENDPOINTS.CHANGE_PASSWORD, passwordData, {
      headers: {
        'Content-Type': 'application/json'
      }
    });

    const result = response.data;
    return result.success ? result.data : result;
  } catch {
    throw new Error('Erreur lors du changement de mot de passe');
  }
};

/**
 * Mettre à jour le profil utilisateur
 * @param {number} userId - ID de l'utilisateur
 * @param {Object} updateData - Données à mettre à jour
 * @returns {Promise<Object>}
 */
export const updateUserProfile = async (userId, updateData) => {
  try {
    const response = await axios.put(`/api/utilisateurs/${userId}`, updateData, {
      headers: {
        'Content-Type': 'application/json'
      }
    });

    const result = response.data;
    return result.success ? result.data : result;
  } catch {
    throw new Error('Erreur lors de la mise à jour du profil');
  }
};

/**
 * Envoyer une demande de création de compte
 * @param {Object} demandeData - Données de la demande
 * @returns {Promise<Object>}
 */
export const requestAccount = async (demandeData) => {
  try {
    const response = await axios.post('/api/auth/request-account', demandeData, {
      headers: {
        'Content-Type': 'application/json'
      }
    });

    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Erreur lors de l\'envoi de la demande');
  }
};
/**
 * Récupérer les sessions actives de l'utilisateur
 * @returns {Promise<Array>}
 */
export const getActiveSessions = async () => {
  try {
    const response = await axios.get('/api/auth/sessions');
    return response.data.success ? response.data.data : response.data;
  } catch {
    throw new Error('Impossible de récupérer les sessions');
  }
};

/**
 * Terminer une session spécifique
 * @param {string} sessionId - ID de la session à terminer
 * @returns {Promise<Object>}
 */
export const terminateSession = async (sessionId) => {
  try {
    const response = await axios.delete(`/api/auth/sessions/${sessionId}`);
    return response.data;
  } catch {
    throw new Error('Impossible de terminer la session');
  }
};

/**
 * Mettre à jour le mot de passe
 * @param {string} currentPassword - Ancien mot de passe
 * @param {string} newPassword - Nouveau mot de passe
 * @returns {Promise<Object>}
 */
export const updatePassword = async (currentPassword, newPassword) => {
  try {
    // Option A: Via l'API backend pour validation du mot de passe actuel
    const response = await axios.post('/api/auth/change-password', {
      currentPassword,
      newPassword
    });
    return response.data;

    /* 
    // Option B: Directement via Supabase (si on ne valide pas l'ancien mot de passe côté serveur)
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw error;
    return { success: true };
    */
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Erreur lors du changement de mot de passe');
  }
};
